<template>
  <div class="flex h-screen bg-gray-50">
    <!-- Support Bot Interface -->
    <div class="flex-1 flex flex-col">
      <!-- Header -->
      <header class="bg-white border-b border-gray-200 px-6 py-4">
        <div class="flex items-center justify-between">
          <div class="flex items-center space-x-3 space-x-reverse">
            <div class="bg-green-100 p-2 rounded-lg">
              <Headphones class="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h1 class="text-lg font-semibold text-gray-800">خدمة العملاء</h1>
              <p class="text-sm text-gray-600">نحن هنا لمساعدتك على مدار الساعة</p>
            </div>
          </div>
          <div class="flex items-center space-x-2 space-x-reverse">
            <div class="flex items-center">
              <div class="w-2 h-2 bg-green-500 rounded-full mr-2"></div>
              <span class="text-sm text-green-600 font-medium">متصل</span>
            </div>
          </div>
        </div>
      </header>

      <!-- Messages Area -->
      <div class="flex-1 overflow-y-auto p-6 space-y-4" ref="messagesContainer">
        <!-- Welcome Message -->
        <div v-if="messages.length === 0" class="flex justify-start">
          <div class="bg-white border border-gray-200 rounded-lg px-4 py-3 max-w-md">
            <div class="flex items-center mb-2">
              <div class="bg-green-100 p-1 rounded-full mr-2">
                <Headphones class="w-4 h-4 text-green-600" />
              </div>
              <span class="text-sm font-medium text-gray-800">بوت الدعم</span>
            </div>
            <p class="text-sm text-gray-700 mb-3">
              مرحباً! أهلاً بك في خدمة العملاء. أنا هنا لمساعدتك في أي أسئلة حول خدماتنا القانونية، المشاكل التقنية، أو أمور الحساب.
            </p>
            <div class="space-y-2">
              <p class="text-sm text-gray-600">كيف يمكنني مساعدتك اليوم؟</p>
              
              <!-- Quick Actions -->
              <div class="grid grid-cols-1 gap-2 mt-3">
                <button
                  v-for="action in quickActions"
                  :key="action.id"
                  @click="sendQuickAction(action)"
                  class="text-left p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-sm"
                >
                  <div class="font-medium text-gray-800">{{ action.title }}</div>
                  <div class="text-xs text-gray-600">{{ action.description }}</div>
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Chat Messages -->
        <div
          v-for="message in messages"
          :key="message.id"
          class="flex"
          :class="message.role === 'user' ? 'justify-start' : 'justify-end'"
        >
          <div
            class="max-w-xs lg:max-w-md px-4 py-3 rounded-lg"
            :class="message.role === 'user' 
              ? 'bg-blue-600 text-white rounded-br-sm' 
              : 'bg-white border border-gray-200 text-gray-800 rounded-bl-sm'"
          >
            <!-- Support Bot Header -->
            <div v-if="message.role === 'assistant'" class="flex items-center mb-2">
              <div class="bg-green-100 p-1 rounded-full mr-2">
                <Headphones class="w-3 h-3 text-green-600" />
              </div>
              <span class="text-xs font-medium">بوت الدعم</span>
            </div>
            
            <p class="text-sm whitespace-pre-wrap">{{ message.content }}</p>
            
            <!-- Support Options -->
            <div v-if="message.options && message.options.length > 0" class="mt-3 space-y-2">
              <button
                v-for="option in message.options"
                :key="option.id"
                @click="selectOption(option)"
                class="w-full text-left p-2 bg-gray-50 rounded hover:bg-gray-100 transition-colors text-xs"
              >
                {{ option.text }}
              </button>
            </div>

            <!-- Satisfaction Rating -->
            <div v-if="message.showRating" class="mt-3 pt-3 border-t border-gray-200">
              <p class="text-xs text-gray-600 mb-2">هل كان هذا مفيداً؟</p>
              <div class="flex space-x-2 space-x-reverse">
                <button
                  v-for="rating in [1, 2, 3, 4, 5]"
                  :key="rating"
                  @click="rateSupport(rating)"
                  class="p-1 rounded hover:bg-gray-100 transition-colors"
                >
                  <Star class="w-4 h-4" :class="rating <= userRating ? 'text-yellow-400 fill-current' : 'text-gray-300'" />
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Typing Indicator -->
        <div v-if="isTyping" class="flex justify-start">
          <div class="bg-white border border-gray-200 rounded-lg px-4 py-3">
            <div class="flex items-center space-x-2 space-x-reverse">
              <div class="flex space-x-1 space-x-reverse">
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
                <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
              </div>
              <span class="text-sm text-gray-600">الدعم يكتب...</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Area -->
      <div class="border-t border-gray-200 bg-white px-6 py-4">
        <!-- Support Categories -->
        <div class="mb-3 flex flex-wrap gap-2">
          <button
            v-for="category in supportCategories"
            :key="category.id"
            @click="selectCategory(category)"
            :class="[
              'px-3 py-1 rounded-full text-xs transition-colors',
              selectedCategory?.id === category.id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            ]"
          >
            {{ category.name }}
          </button>
        </div>

        <div class="flex space-x-2 space-x-reverse">
          <input
            v-model="inputMessage"
            type="text"
            placeholder="اكتب سؤالك أو مشكلتك..."
            class="flex-1 px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            @keypress="handleKeyPress"
            :disabled="isTyping"
          />
          <button
            @click="sendMessage"
            :disabled="isTyping || !inputMessage.trim()"
            class="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <Send class="w-5 h-5" />
          </button>
        </div>

        <!-- Support Info -->
        <div class="mt-3 flex items-center justify-between text-xs text-gray-500">
          <div class="flex items-center">
            <Clock class="w-3 h-3 mr-1" />
            <span>متوسط وقت الاستجابة: دقيقتان</span>
          </div>
          <div class="flex items-center">
            <Users class="w-3 h-3 mr-1" />
            <span>245 وكيل دعم متصل</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue'
import { Send, Headphones, Star, Clock, Users } from 'lucide-vue-next'

interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  options?: Array<{
    id: string
    text: string
    action?: string
  }>
  showRating?: boolean
  timestamp: Date
}

interface QuickAction {
  id: string
  title: string
  description: string
  action: string
}

interface SupportCategory {
  id: string
  name: string
  description: string
}

const messages = ref<Message[]>([])
const inputMessage = ref('')
const isTyping = ref(false)
const messagesContainer = ref<HTMLElement>()
const selectedCategory = ref<SupportCategory | null>(null)
const userRating = ref(0)

const quickActions: QuickAction[] = [
  {
    id: '1',
    title: 'الدعم التقني',
    description: 'مساعدة في ميزات التطبيق، مشاكل تسجيل الدخول، أو المشاكل التقنية',
    action: 'technical'
  },
  {
    id: '2', 
    title: 'الفواتير والحساب',
    description: 'أسئلة حول الاشتراك، المدفوعات، أو إعدادات الحساب',
    action: 'billing'
  },
  {
    id: '3',
    title: 'الخدمات القانونية',
    description: 'معلومات حول خدمات الاستشارة القانونية',
    action: 'legal'
  },
  {
    id: '4',
    title: 'الإبلاغ عن مشكلة',
    description: 'الإبلاغ عن الأخطاء، المشاكل، أو القضايا الأخرى',
    action: 'report'
  }
]

const supportCategories: SupportCategory[] = [
  { id: 'general', name: 'عام', description: 'أسئلة عامة' },
  { id: 'technical', name: 'تقني', description: 'المشاكل التقنية' },
  { id: 'billing', name: 'فواتير', description: 'الدفع والاشتراك' },
  { id: 'legal', name: 'خدمات قانونية', description: 'الاستشارة القانونية' },
  { id: 'account', name: 'حساب', description: 'إدارة الحساب' }
]

const sendQuickAction = (action: QuickAction) => {
  const userMessage: Message = {
    id: `user-${Date.now()}`,
    role: 'user',
    content: action.title,
    timestamp: new Date()
  }
  
  messages.value.push(userMessage)
  scrollToBottom()
  
  // Simulate bot response
  setTimeout(() => {
    handleSupportRequest(action.action)
  }, 1000)
}

const selectCategory = (category: SupportCategory) => {
  selectedCategory.value = category
  
  const userMessage: Message = {
    id: `user-${Date.now()}`,
    role: 'user',
    content: `أحتاج مساعدة في ${category.name}`,
    timestamp: new Date()
  }
  
  messages.value.push(userMessage)
  scrollToBottom()
  
  setTimeout(() => {
    handleCategorySupport(category.id)
  }, 1000)
}

const handleSupportRequest = (action: string) => {
  isTyping.value = true
  
  setTimeout(() => {
    const responses: Record<string, Message> = {
      technical: {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: `يمكنني مساعدتك في المشاكل التقنية! المشاكل الشائعة التي يمكنني مساعدتك فيها:\n\n1. مشاكل تسجيل الدخول والمصادقة\n2. مشاكل أداء التطبيق\n3. مشاكل رفع/تحميل الملفات\n4. أسئلة استخدام الميزات\n\nما هي المشكلة التقنية المحددة التي تواجهها؟`,
        options: [
          { id: '1', text: 'مشاكل تسجيل الدخول', action: 'login' },
          { id: '2', text: 'مشاكل رفع الملفات', action: 'upload' },
          { id: '3', text: 'أداء التطبيق', action: 'performance' },
          { id: '4', text: 'مشكلة تقنية أخرى', action: 'other' }
        ],
        timestamp: new Date()
      },
      billing: {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: `أنا هنا لمساعدتك في أسئلة الفواتير والحساب. يمكنني مساعدتك في:\n\n1. خطط الاشتراك والأسعار\n2. طرق الدفع والفواتير\n3. ترقيات/خفضات الحساب\n4. طلبات استرداد الأموال\n\nما هي مسألة الفواتير التي يمكنني مساعدتك فيها؟`,
        options: [
          { id: '1', text: 'خطط الاشتراك', action: 'plans' },
          { id: '2', text: 'مشاكل الدفع', action: 'payment' },
          { id: '3', text: 'طلب استرداد الأموال', action: 'refund' }
        ],
        timestamp: new Date()
      },
      legal: {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: `يسعدني تقديم معلومات حول خدماتنا القانونية! نحن نقدم:\n\n1. استشارة قانونية مدعومة بالذكاء الاصطناعي\n2. تحليل ومراجعة المستندات\n3. خدمات مطابقة المحامين\n4. مساعدة البحث القانوني\n\nأي خدمة ترغب في معرفة المزيد عنها؟`,
        options: [
          { id: '1', text: 'استشارة قانونية بالذكاء الاصطناعي', action: 'ai-consult' },
          { id: '2', text: 'تحليل المستندات', action: 'docs' },
          { id: '3', text: 'مطابقة المحامين', action: 'lawyers' },
          { id: '4', text: 'معلومات الأسعار', action: 'pricing' }
        ],
        timestamp: new Date()
      },
      report: {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: `أنا آسف لأنك تواجه مشاكل. لمساعدتك بشكل أفضل، هل يمكنك توفير:\n\n1. ما هي المشكلة المحددة التي تواجهها؟\n2. متى بدأت هذه المشكلة؟\n3. ما هو الجهاز/المتصفح الذي تستخدمه؟\n4. أي رسائل خطأ تراها\n\nهذه المعلومات سوف تساعدني في حل مشكلتك بشكل أسرع.`,
        timestamp: new Date()
      }
    }
    
    const response = responses[action] || responses.technical
    messages.value.push(response)
    isTyping.value = false
    scrollToBottom()
  }, 2000)
}

const handleCategorySupport = (_categoryId: string) => {
  isTyping.value = true
  
  setTimeout(() => {
    const response: Message = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: `I understand you need help with ${selectedCategory.value?.name}. Let me connect you with the right resources and provide personalized assistance for your ${selectedCategory.value?.description.toLowerCase()}.\n\nPlease describe your specific issue in detail so I can help you better.`,
      timestamp: new Date()
    }
    
    messages.value.push(response)
    isTyping.value = false
    scrollToBottom()
  }, 1500)
}

const selectOption = (option: any) => {
  const userMessage: Message = {
    id: `user-${Date.now()}`,
    role: 'user',
    content: option.text,
    timestamp: new Date()
  }
  
  messages.value.push(userMessage)
  scrollToBottom()
  
  // Handle specific actions
  if (option.action) {
    setTimeout(() => {
      handleSpecificAction(option.action)
    }, 1000)
  }
}

const handleSpecificAction = (action: string) => {
  isTyping.value = true
  
  setTimeout(() => {
    const response: Message = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: getActionResponse(action),
      showRating: true,
      timestamp: new Date()
    }
    
    messages.value.push(response)
    isTyping.value = false
    scrollToBottom()
  }, 1500)
}

const getActionResponse = (action: string): string => {
  const responses: Record<string, string> = {
    login: 'For login issues, try these steps:\n1. Clear your browser cache and cookies\n2. Reset your password using the "Forgot Password" link\n3. Disable browser extensions temporarily\n4. Try a different browser\n\nIf the issue persists, I can escalate you to our technical team.',
    upload: 'For file upload issues:\n1. Check file size (max 10MB)\n2. Ensure file format is supported (PDF, Word, Images)\n3. Try refreshing the page\n4. Clear browser cache\n\nWhich step would you like to try first?',
    performance: 'To improve app performance:\n1. Close unnecessary browser tabs\n2. Clear browser cache\n3. Check your internet connection speed\n4. Update your browser to the latest version\n\nLet me know if you need more specific optimization tips.',
    plans: 'We offer three subscription plans:\n\n1. Basic: $9.99/month - 10 legal consultations\n2. Professional: $29.99/month - Unlimited consultations + document analysis\n3. Enterprise: Custom pricing - Team features + priority support\n\nWhich plan interests you?',
    payment: 'For payment issues:\n1. Check if your card details are correct\n2. Ensure your bank allows international transactions\n3. Try a different payment method\n4. Contact your bank if the transaction is declined\n\nI can also help you update your payment method.',
    refund: 'Our refund policy:\n- 30-day money-back guarantee for new subscriptions\n- Pro-rated refunds for annual plans\n- Refunds processed within 5-7 business days\n\nWould you like me to initiate a refund request?',
    'ai-consult': 'Our AI Legal Consultation provides:\n- Instant legal advice on various topics\n- Document analysis and review\n- Legal research assistance\n- Available 24/7\n\nAccuracy rate: 94% based on user feedback.\n\nWhat legal topic would you like help with?',
    docs: 'Document Analysis features:\n- Contract review and risk assessment\n- Legal document summarization\n- Clause extraction and analysis\n- Compliance checking\n\nUpload a document to get started with AI analysis.',
    lawyers: 'Lawyer Matching Service:\n- Connect with verified legal professionals\n- Specialized matching based on your needs\n- Background-checked lawyers\n- Satisfaction guarantee\n\nWhat type of legal matter do you need help with?',
    pricing: 'Our pricing is transparent:\n- Pay-per-consultation: $49/session\n- Monthly subscription: $29.99/month\n- Annual subscription: $299/year (save 17%)\n- Custom enterprise pricing available\n\nWhich option fits your needs best?'
  }
  
  return responses[action] || 'I understand your request. Let me help you with this issue. Could you provide more details so I can assist you better?'
}

const rateSupport = (rating: number) => {
  userRating.value = rating
  
  const ratingMessage: Message = {
    id: `assistant-${Date.now()}`,
    role: 'assistant',
    content: rating >= 4 
      ? 'Thank you for your feedback! I\'m glad I could help you. Is there anything else I can assist you with?'
      : 'I apologize that I didn\'t fully meet your expectations. I\'m here to help - could you let me know how I can improve my assistance?',
    timestamp: new Date()
  }
  
  messages.value.push(ratingMessage)
  scrollToBottom()
}

const sendMessage = () => {
  if (!inputMessage.value.trim() || isTyping.value) return

  const userMessage: Message = {
    id: `user-${Date.now()}`,
    role: 'user',
    content: inputMessage.value.trim(),
    timestamp: new Date()
  }

  messages.value.push(userMessage)
  inputMessage.value = ''
  scrollToBottom()

  // Simulate bot response
  isTyping.value = true
  setTimeout(() => {
    const botResponse: Message = {
      id: `assistant-${Date.now()}`,
      role: 'assistant',
      content: generateBotResponse(userMessage.content),
      timestamp: new Date()
    }
    
    messages.value.push(botResponse)
    isTyping.value = false
    scrollToBottom()
  }, 1500)
}

const generateBotResponse = (userInput: string): string => {
  const input = userInput.toLowerCase()
  
  if (input.includes('hello') || input.includes('hi')) {
    return 'Hello! How can I assist you today? I\'m here to help with any questions about our services or technical issues.'
  } else if (input.includes('help')) {
    return 'I\'m here to help! I can assist you with:\n\n1. Technical support and troubleshooting\n2. Billing and account questions\n3. Legal services information\n4. General inquiries\n\nWhat do you need help with?'
  } else if (input.includes('price') || input.includes('cost')) {
    return 'Our pricing starts at $9.99/month for basic access. We offer flexible plans to suit different needs. Would you like detailed pricing information?'
  } else if (input.includes('lawyer') || input.includes('attorney')) {
    return 'We can connect you with verified legal professionals. Our lawyer matching service considers your specific legal needs and location. What type of legal matter are you dealing with?'
  } else if (input.includes('document') || input.includes('file')) {
    return 'Our AI can analyze legal documents including contracts, agreements, and legal papers. Simply upload your document for instant analysis and insights.'
  } else if (input.includes('refund') || input.includes('money back')) {
    return 'We offer a 30-day money-back guarantee for new subscriptions. Refunds are typically processed within 5-7 business days. Would you like me to help you with a refund request?'
  } else if (input.includes('bug') || input.includes('error') || input.includes('problem')) {
    return 'I\'m sorry you\'re experiencing issues. Could you please provide more details about the problem you\'re encountering? This will help me assist you better.'
  } else {
    return 'I understand your question. Let me help you with that. Could you provide a bit more detail so I can give you the most accurate assistance?'
  }
}

const handleKeyPress = (event: KeyboardEvent) => {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    sendMessage()
  }
}

const scrollToBottom = () => {
  nextTick(() => {
    if (messagesContainer.value) {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  })
}
</script>

<style scoped>
@keyframes bounce {
  0%, 80%, 100% {
    transform: scale(0);
  }
  40% {
    transform: scale(1);
  }
}
</style>
